import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  PlayArrow as PlayIcon,
  Code as CodeIcon,
  Add as AddIcon,
  Edit as EditIcon,
  StorageOutlined as StorageOutlinedIcon,
} from '@mui/icons-material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import TenantLoginEditor from '../components/TenantLoginEditor';

/** Purpose: Perform format created date for the bot selector module. Why: Callers need a consistent result from this shared operation. */
const formatCreatedDate = (value) => {
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
};

/** Purpose: Perform format bytes for the bot selector module. Why: Callers need a consistent result from this shared operation. */
const formatBytes = (bytes = 0) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const unitIndex = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / (1024 ** unitIndex)).toFixed(unitIndex === 0 ? 0 : 2)} ${units[unitIndex]}`;
};

/** Purpose: let a user choose an accessible bot workspace. Why: multi-bot accounts must select a tenant before bot-scoped tools can load. */
export default function BotSelector() {
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [botToRename, setBotToRename] = useState(null);
  const [renameForm, setRenameForm] = useState({ botName: '', description: '' });
  const [savingRename, setSavingRename] = useState(false);
  const [prodDialogBot, setProdDialogBot] = useState(null);
  const [creatingProd, setCreatingProd] = useState(false);
  const [storageByBotId, setStorageByBotId] = useState({});
  const { user, isAdmin, selectBot } = useAuth();
  const navigateRouter = useNavigate();
  
  // Request deduplication - prevent multiple simultaneous requests
  const fetchBotsPromise = useRef(null);
  const hasFetched = useRef(false);

  /** Purpose: Load fetch bots for the bot selector module. Why: The selected view or service needs current scoped data before continuing. */
  const fetchBots = async () => {
    // If a request is already in progress, return the existing promise
    if (fetchBotsPromise.current) {
      return fetchBotsPromise.current;
    }
    
    // If already fetched in this session, skip
    if (hasFetched.current) {
      return;
    }

    // Create new request promise
    fetchBotsPromise.current = (async () => {
      try {
        const response = await axios.get('/api/v1/bots');
        setBots(response.data.bots || []);
        setError(null);
        hasFetched.current = true;
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load bots');
        // Reset on error so we can retry
        hasFetched.current = false;
      } finally {
        setLoading(false);
        // Clear the promise after request completes
        fetchBotsPromise.current = null;
      }
    })();

    return fetchBotsPromise.current;
  };

  useEffect(() => {
    // Only fetch once on mount, and only if we have a token
    const token = localStorage.getItem('token');
    if (!hasFetched.current && token) {
      fetchBots();
    } else if (!token) {
      setLoading(false);
      setError('Please login to view bots');
    }
  }, []);

  useEffect(() => {
    if (!user || !isAdmin()) return;
    axios.get('/api/v1/admin/storage-usage')
      .then((response) => {
        const usageMap = Object.fromEntries((response.data?.bots || []).map((bot) => [bot.botId, bot]));
        setStorageByBotId(usageMap);
      })
      .catch(() => {
        // Bot selection must remain usable even if optional storage stats fail.
        setStorageByBotId({});
      });
  }, [user]);

  /** Purpose: Handle handle bot select in the bot selector module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleBotSelect = (botId) => {
    selectBot(botId);
    navigateRouter(`/dashboard`);
  };

  /** Purpose: Handle open rename dialog in the bot selector module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const openRenameDialog = (bot) => {
    setBotToRename(bot);
    setRenameForm({
      botName: bot.botName || '',
      description: bot.description || '',
    });
    setRenameDialogOpen(true);
  };

  /** Purpose: Handle handle rename bot in the bot selector module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleRenameBot = async () => {
    if (!botToRename || !renameForm.botName.trim()) return;

    setSavingRename(true);
    try {
      const response = await axios.put(`/api/v1/admin/bots/${botToRename.botId}`, {
        botName: renameForm.botName.trim(),
        description: renameForm.description || '',
      });

      const updatedBot = response.data?.bot;
      setBots((current) =>
        current.map((bot) =>
          bot.botId === botToRename.botId
            ? {
                ...bot,
                botName: updatedBot?.botName || renameForm.botName.trim(),
                description: updatedBot?.description ?? renameForm.description ?? '',
              }
            : bot
        )
      );
      setRenameDialogOpen(false);
      setBotToRename(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to rename bot');
    } finally {
      setSavingRename(false);
    }
  };

  /** Purpose: Handle handle create prod in the bot selector module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleCreateProd = async () => {
    if (!prodDialogBot) return;
    setCreatingProd(true);
    setError(null);
    try {
      const response = await axios.post(`/api/v1/environments/${prodDialogBot.botId}/prod`);
      const environment = response.data?.environment;
      setBots((current) => current.map((bot) => bot.botId === prodDialogBot.botId
        ? {
            ...bot,
            environments: [...(bot.environments || []), environment],
            environmentsAllowed: Array.from(new Set([...(bot.environmentsAllowed || []), 'prod'])),
          }
        : bot));
      setProdDialogBot(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create PROD environment');
    } finally {
      setCreatingProd(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} mb={4}>
        <Box>
          <Typography variant="h4" component="h1">
            Select a Bot
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Pick a workspace to open its dashboard, flows, and settings.
          </Typography>
        </Box>
        {isAdmin() && (
          <Button
            variant="contained"
            onClick={() => navigateRouter('/admin')}
            startIcon={<SettingsIcon />}
          >
            Admin Panel
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {bots.length === 0 ? (
        <Alert severity="info">
          {isAdmin()
            ? 'No bots found. Create your first bot from the Admin Panel.'
            : 'You don\'t have access to any bots. Contact your administrator.'}
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {bots.map((bot) => (
            <Grid item xs={12} sm={6} lg={4} key={bot.botId}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                }}
                onClick={() => handleBotSelect(bot.botId)}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="h6" component="h2" sx={{ wordBreak: 'break-word' }}>
                        {bot.botName}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {isAdmin() && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            openRenameDialog(bot);
                          }}
                          aria-label="Edit bot"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                      <Chip
                        label={bot.status}
                        color={bot.status === 'active' ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>
                  </Box>

                  {bot.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {bot.description}
                    </Typography>
                  )}

                  <Box mt={2}>
                    <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                      Available Environments:
                    </Typography>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      {bot.environments.map((env) => {
                        const hasAccess = bot.environmentsAllowed.includes(env.envName) || isAdmin();
                        return (
                          <Chip
                            key={env.envId}
                            label={env.envName.toUpperCase()}
                            color={hasAccess ? 'primary' : 'default'}
                            size="small"
                            variant={hasAccess ? 'filled' : 'outlined'}
                          />
                        );
                      })}
                    </Box>
                    {isAdmin()
                      && bot.environments?.some((env) => env.envName === 'dev')
                      && !bot.environments?.some((env) => env.envName === 'prod') && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AddIcon />}
                          sx={{ mt: 1.25 }}
                          onClick={(event) => {
                            event.stopPropagation();
                            setProdDialogBot(bot);
                          }}
                        >
                          Create PROD
                        </Button>
                      )}
                  </Box>

                  <Box mt={2} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {bot.createdBy && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Created by: {bot.createdBy.name || bot.createdBy.email}
                      </Typography>
                    )}
                    {formatCreatedDate(bot.createdAt) && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Created on: {formatCreatedDate(bot.createdAt)}
                      </Typography>
                    )}
                    {bot.planName && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Plan: <Box component="span" sx={{ color: 'text.primary', fontWeight: 700 }}>{bot.planName}</Box>
                      </Typography>
                    )}
                    {isAdmin() && storageByBotId[bot.botId] && (
                      <Box
                        sx={{ mt: 0.75, px: 1, py: 0.75, borderRadius: 2, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', gap: 0.75 }}
                      >
                        <StorageOutlinedIcon sx={{ fontSize: 17, color: 'primary.main' }} />
                        <Typography variant="caption" color="text.secondary">
                          Mongo usage: <Box component="span" sx={{ color: 'text.primary', fontWeight: 800 }}>{formatBytes(storageByBotId[bot.botId].dataBytes)}</Box>
                          {' · '}{storageByBotId[bot.botId].documentCount.toLocaleString()} documents
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>

                <CardActions>
                  <Button
                    size="small"
                    startIcon={<PlayIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleBotSelect(bot.botId);
                    }}
                  >
                    Open
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}

          {isAdmin() && (
            <Grid item xs={12} sm={6} lg={4}>
              <Card
                sx={{
                  height: '100%',
                  minHeight: 220,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  border: '1px dashed rgba(59, 130, 246, 0.35)',
                  bgcolor: 'rgba(255,255,255,0.75)',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                }}
                onClick={() => navigateRouter('/admin')}
              >
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    mb: 2,
                  }}
                >
                  <AddIcon />
                </Box>
                <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>
                  Create New Bot
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1, px: 3 }}>
                  Start a new workspace and assign it to your team from the admin panel.
                </Typography>
                <Button sx={{ mt: 2 }} variant="outlined">
                  Go to Admin Panel
                </Button>
              </Card>
            </Grid>
          )}
        </Grid>
      )}

      <Dialog open={renameDialogOpen} onClose={() => setRenameDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Bot</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Bot Name"
            value={renameForm.botName}
            onChange={(e) => setRenameForm({ ...renameForm, botName: e.target.value })}
            margin="normal"
            autoFocus
          />
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Description"
            value={renameForm.description}
            onChange={(e) => setRenameForm({ ...renameForm, description: e.target.value })}
            margin="normal"
            placeholder="Optional description"
          />
          {isAdmin() && renameDialogOpen && botToRename && <TenantLoginEditor key={botToRename.botId} botId={botToRename.botId} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleRenameBot}
            variant="contained"
            disabled={savingRename || !renameForm.botName.trim()}
          >
            {savingRename ? <CircularProgress size={20} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(prodDialogBot)} onClose={() => !creatingProd && setProdDialogBot(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Create PROD Environment</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 1 }}>
            <strong>{prodDialogBot?.botName}</strong> ka current DEV configuration copy karke independent PROD environment create hoga. Safety ke liye WhatsApp/Voice delivery disabled rahegi; PROD credentials aur webhook review karne ke baad Settings se enable karein.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button disabled={creatingProd} onClick={() => setProdDialogBot(null)}>Cancel</Button>
          <Button variant="contained" disabled={creatingProd} onClick={handleCreateProd} startIcon={creatingProd ? <CircularProgress size={16} /> : <AddIcon />}>
            {creatingProd ? 'Creating…' : 'Create PROD'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
